import { IconName } from '@/components/Icon';

import { Tenant, TenantWithDefault } from '@/types/common';
import {
  DocumentSection,
  DocumentStatus,
  DocumentType,
} from '@/types/document';
import { Language } from '@/types/i18n';

import { REQUEST } from './api';

export const GROUPED_CAYMAN_DOCUMENTS = [
  DocumentType.LetterOfEmployment,
  DocumentType.LeaseAgreement,
];

export const DOCUMENT_INACTIVE_STATUSES: DocumentStatus[] = [
  DocumentStatus.Rejected,
  DocumentStatus.Cancelled,
  DocumentStatus.Signing,
  DocumentStatus.Requested,
];

export const DOCUMENT_COMPLETED_STATUSES: DocumentStatus[] = [
  DocumentStatus.InReview,
  DocumentStatus.Approved,
  DocumentStatus.Completed,
  DocumentStatus.Rejected,
];

export const DOCUMENT_SECTION_ORDER: Partial<Record<DocumentSection, number>> =
  {
    [DocumentSection.Identity]: 1,
    [DocumentSection.IdentityBack]: 2,
    [DocumentSection.Selfie]: 3,
    [DocumentSection.TIN]: 4,
    [DocumentSection.ProofOfAddress]: 5,
    [DocumentSection.ProofOfEmployment]: 6,
    [DocumentSection.SecondID]: 7,
    [DocumentSection.DocumentSign]: 8,
    [DocumentSection.IDVideo]: 9,
    [DocumentSection.USCitizenshipRenouncement]: 10,
    [DocumentSection.TaxSupportingDocument]: 11,
  };

export const DOCUMENT_SECTION_DESCRIPTION: Partial<
  Record<DocumentSection, string>
> = {};

export const DOCUMENT_TYPE_HTML: Record<string, string> = {
  [DocumentType.UtilityBill]: `documentTypeHTML.${[DocumentType.UtilityBill]}`,
  [DocumentType.UtilityBillNonOwner]: `documentTypeHTML.${[DocumentType.UtilityBillNonOwner]}`,
  [DocumentType.SourceOfFunds]: `documentTypeHTML.${[DocumentType.SourceOfFunds]}`,
  [DocumentType.Other]: `documentTypeHTML.${[DocumentType.Other]}`,
};

export const DOCUMENT_SUBTITLE_BY_TYPE: Partial<
  Record<DocumentType, Partial<Record<TenantWithDefault, string>>>
> = {
  [DocumentType.UtilityBill]: {
    default: 'documentTypeSubtitle.ofLessThan3MonthsParens',
    [Tenant.Guyana]: 'documentTypeSubtitle.ofLessThan8MonthsParens',
    [Tenant.Trinidad]: 'documentTypeSubtitle.ofLessThan6MonthsParens',
  },
  [DocumentType.UtilityBillNonOwner]: {
    default: 'documentTypeSubtitle.ofLessThan3MonthsParens',
    [Tenant.Guyana]: 'documentTypeSubtitle.ofLessThan8MonthsParens',
    [Tenant.Trinidad]: 'documentTypeSubtitle.ofLessThan6MonthsParens',
  },
  [DocumentType.CBBExtract]: {
    [Tenant.Suriname]: 'documentTypeSubtitle.issuedWithin3MonthsOr7Months',
  },
  [DocumentType.BankStatement]: {
    default: 'documentTypeSubtitle.issuedWithinLast3Months',
  },
  [DocumentType.ProofOfCurrentAddress]: {
    default: 'documentTypeSubtitle.datedWithinLast3MonthsParens',
  },
  [DocumentType.TaxExemptionDocument]: {
    default: 'documentTypeSubtitle.taxExemptionDocumentExamples',
  },
  [DocumentType.LocalBankStatement]: {
    default: 'documentTypeSubtitle.issuedWithinLast3Months',
  },
  [DocumentType.ForeignBankStatement]: {
    default: 'documentTypeSubtitle.issuedWithinLast3Months',
  },
  [DocumentType.CertificateOfTitle]: {
    [Tenant.Guyana]: 'documentTypeSubtitle.ofLessThan8MonthsParens',
  },
  [DocumentType.FireInsurance]: {
    [Tenant.Guyana]: 'documentTypeSubtitle.ofLessThan8MonthsParens',
  },
  [DocumentType.LetterOfAuthorisationFromUtilityBillOwner]: {
    [Tenant.Guyana]: 'documentTypeSubtitle.ofLessThan8MonthsParens',
    [Tenant.Cayman]: 'documentTypeSubtitle.downloadAndSignParens',
  },
  [DocumentType.LeaseAgreement]: {
    [Tenant.Guyana]: 'documentTypeSubtitle.ofLessThan8MonthsParens',
  },
  [DocumentType.LetterFromToshaosHinterlandLocations]: {
    [Tenant.Guyana]: 'documentTypeSubtitle.ofLessThan8MonthsParens',
  },
  [DocumentType.LetterOfEmployment]: {
    default: 'documentTypeSubtitle.issuedWithinLast3Months',
  },
  [DocumentType.PostMarkedEnvelopeWithCustomerNameAndAddress]: {
    [Tenant.Guyana]: 'documentTypeSubtitle.ofLessThan8MonthsParens',
  },
  [DocumentType.RatesAndTaxesReceiptInvoice]: {
    [Tenant.Guyana]: 'documentTypeSubtitle.ofLessThan8MonthsParens',
  },
  [DocumentType.RecentPayslip]: {
    default: 'documentTypeSubtitle.issuedWithinLast3Months',
  },
  [DocumentType.TinCertificate]: {
    [Tenant.Guyana]: 'documentTypeSubtitle.ofLessThan8MonthsParens',
  },
  [DocumentType.TradeLicense]: {
    default: 'documentTypeSubtitle.forThePast3MonthsParens',
  },
  [DocumentType.FinancialStatements]: {
    [Tenant.Suriname]: 'documentTypeSubtitle.forThePast3YearsParens',
    [Tenant.Trinidad]: 'documentTypeSubtitle.forThePast3YearsParens',
    [Tenant.SintMaarten]: 'documentTypeSubtitle.forThePast3YearsParens',
  },
  [DocumentType.FinancialStatementsProjections]: {
    default: 'documentTypeSubtitle.twoYearProjectedPlanParens',
  },
  [DocumentType.ForecastProjections]: {
    default: 'documentTypeSubtitle.forTheNext3YearsNoLaterThan3MonthsParens',
  },
  [DocumentType.BankReferenceLetter]: {
    default: 'documentTypeSubtitle.issuedWithinLast3Months',
  },
  [DocumentType.LocalBankReferenceLetter]: {
    default: 'documentTypeSubtitle.issuedWithinLast3Months',
  },
  [DocumentType.ForeignBankReferenceLetter]: {
    default: 'documentTypeSubtitle.issuedWithinLast3Months',
  },
  [DocumentType.ManagementAccounts]: {
    [Tenant.Trinidad]: 'documentTypeSubtitle.forThePast3YearsParens',
    [Tenant.SintMaarten]: 'documentTypeSubtitle.forThePast3YearsParens',
  },
  [DocumentType.MonthlyIncomeAndExpenditure]: {
    [Tenant.Trinidad]: 'documentTypeSubtitle.downloadAndSignParens',
  },
  [DocumentType.NotarizedStatement]: {
    [Tenant.Cayman]: 'documentTypeSubtitle.forAccidentalUSCitizens',
  },
  [DocumentType.Other]: {
    [Tenant.Cayman]: 'documentTypeSubtitle.retailAgreementAndEmploymentLetter',
  },
  [DocumentType.SocialSecurityLetter]: {
    [Tenant.SintMaarten]: 'documentTypeSubtitle.szv',
  },
  [DocumentType.ProjectedIncomeStatement]: {
    [Tenant.SintMaarten]:
      'documentTypeSubtitle.for3YearsIncludingCurrentYearParens',
  },
  [DocumentType.SourceOfFunds]: {
    [Tenant.SintMaarten]: 'documentTypeSubtitle.otherFundingSources',
  },
  [DocumentType.ProfessionalRecommendationLetter]: {
    [Tenant.SintMaarten]: 'documentTypeSubtitle.professionalRecommendation',
  },
  [DocumentType.ProjectedCashFlows]: {
    [Tenant.Suriname]:
      'documentTypeSubtitle.for3YearsIncludingCurrentYearParens',
  },
};
export const DOCUMENT_DESCRIPTION_BY_TYPE: Partial<
  Record<
    DocumentType,
    Partial<
      Record<
        TenantWithDefault,
        | string
        | {
            national: string;
            nonNational: string;
          }
      >
    >
  >
> = {
  [DocumentType.UtilityBillNonOwner]: {
    default: `documentDescription.${[DocumentType.UtilityBillNonOwner]}`,
    [Tenant.SintMaarten]: {
      national: `documentDescription.${[DocumentType.UtilityBillNonOwner]}.national`,
      nonNational: `documentDescription.${[DocumentType.UtilityBillNonOwner]}.nonNational`,
    },
  },
  [DocumentType.UtilityBill]: {
    default: ``,
    [Tenant.Cayman]: `documentDescription.${[DocumentType.UtilityBill]}`,
  },
  [DocumentType.LetterOfAuthorisationFromUtilityBillOwner]: {
    default: `documentDescription.${[DocumentType.LetterOfAuthorisationFromUtilityBillOwner]}`,
  },
  [DocumentType.SelfAttestationForProofOfAddress]: {
    default: `documentDescription.${[DocumentType.SelfAttestationForProofOfAddress]}`,
  },
  [DocumentType.BankReferenceLetter]: {
    default: `documentDescription.${[DocumentType.BankReferenceLetter]}`,
  },
  [DocumentType.LocalBankReferenceLetter]: {
    default: `documentDescription.${[DocumentType.LocalBankReferenceLetter]}`,
  },
  [DocumentType.ForeignBankReferenceLetter]: {
    default: `documentDescription.${[DocumentType.ForeignBankReferenceLetter]}`,
  },
  [DocumentType.BankStatement]: {
    default: `documentDescription.${[DocumentType.BankStatement]}`,
  },
  [DocumentType.LocalBankStatement]: {
    default: `documentDescription.${[DocumentType.LocalBankStatement]}`,
  },
  [DocumentType.ForeignBankStatement]: {
    default: `documentDescription.${[DocumentType.ForeignBankStatement]}`,
  },
  [DocumentType.ConfirmationOfAddress]: {
    [Tenant.Grenada]: `documentDescription.${[DocumentType.ConfirmationOfAddress]}`,
  },
  [DocumentType.ProfessionalRecommendationLetter]: {
    default: `documentDescription.${[DocumentType.ProfessionalRecommendationLetter]}`,
  },
  [DocumentType.SourceOfFunds]: {
    [Tenant.Cayman]: `documentDescription.${[DocumentType.SourceOfFunds]}`,
    [Tenant.SintMaarten]: `documentDescription.${[DocumentType.SourceOfFunds]}`,
  },
  [DocumentType.SocialSecurityLetter]: {
    [Tenant.SintMaarten]: `documentDescription.${[DocumentType.SocialSecurityLetter]}`,
  },
  [DocumentType.MonthlyIncomeAndExpenditure]: {
    [Tenant.SintMaarten]: `documentDescription.${[DocumentType.MonthlyIncomeAndExpenditure]}`,
  },
  [DocumentType.NotarizedStatement]: {
    [Tenant.Cayman]: `documentDescription.${[DocumentType.NotarizedStatement]}`,
  },
  [DocumentType.LeaseAgreement]: {
    default: `documentDescription.${[DocumentType.LeaseAgreement]}`,
  },
};

export const DOCUMENT_DOWNLOAD_PATH_BY_TYPE: Partial<
  Record<
    DocumentType,
    { [Language.English]: Partial<Record<Tenant | 'default', string>> } & {
      [Language.Dutch]?: Partial<Record<Tenant | 'default', string>>;
    }
  >
> = {
  [DocumentType.ConfirmationOfAddress]: {
    [Language.English]: {
      default: REQUEST.DOWNLOAD_TEMPLATE.CONFIRMATION_OF_ADDRESS,
    },
  },
  [DocumentType.UtilityBillNonOwner]: {
    [Language.English]: {
      [Tenant.Cayman]:
        REQUEST.DOWNLOAD_TEMPLATE
          .AUTHORIZATION_LETTER_FROM_UTILITY_BILL_NON_OWNER_EN_CYM,
    },
  },
  [DocumentType.LetterOfAuthorisationFromUtilityBillOwner]: {
    [Language.English]: {
      default:
        REQUEST.DOWNLOAD_TEMPLATE
          .AUTHORIZATION_LETTER_FROM_UTILITY_BILL_NON_OWNER_EN,
      [Tenant.Cayman]:
        REQUEST.DOWNLOAD_TEMPLATE
          .AUTHORIZATION_LETTER_FROM_UTILITY_BILL_NON_OWNER_EN_CYM,
    },
    [Language.Dutch]: {
      default:
        REQUEST.DOWNLOAD_TEMPLATE
          .AUTHORIZATION_LETTER_FROM_UTILITY_BILL_NON_OWNER_NL,
    },
  },
  [DocumentType.SelfAttestationForProofOfIncome]: {
    [Language.English]: {
      default: REQUEST.DOWNLOAD_TEMPLATE.SELF_ATTESTATION_INCOME,
      [Tenant.Suriname]:
        REQUEST.DOWNLOAD_TEMPLATE.SELF_ATTESTATION_INCOME_EN_SUR,
    },
    [Language.Dutch]: {
      default: REQUEST.DOWNLOAD_TEMPLATE.SELF_ATTESTATION_INCOME,
      [Tenant.Suriname]:
        REQUEST.DOWNLOAD_TEMPLATE.SELF_ATTESTATION_INCOME_NL_SUR,
    },
  },
  [DocumentType.SelfAttestationForProofOfAddress]: {
    [Language.English]: {
      default: REQUEST.DOWNLOAD_TEMPLATE.SELF_ATTESTATION_ADDRESS,
    },
  },
  [DocumentType.MonthlyIncomeAndExpenditure]: {
    [Language.English]: {
      default: REQUEST.DOWNLOAD_TEMPLATE.MONTHLY_INCOME_AND_EXPENDITURE,
    },
  },
  [DocumentType.SelfEmployedIncomeVerification]: {
    [Language.English]: {
      default: '',
      [Tenant.BVI]: REQUEST.DOWNLOAD_TEMPLATE.SELF_EMPLOYED_INCOME_VERIFICATION,
    },
  },
};

export const DOCUMENT_ICON_BY_TYPE: Partial<Record<DocumentType, IconName>> = {
  [DocumentType.UtilityBill]: 'bulb',
  [DocumentType.UtilityBillNonOwner]: 'bulb',
  [DocumentType.BankReferenceLetter]: 'bank',
  [DocumentType.BankStatement]: 'receipt',
  [DocumentType.ProfessionalRecommendationLetter]: 'star',
  [DocumentType.TinCertificate]: 'user',
  [DocumentType.DriverLicense]: 'car-simple-outline',
  [DocumentType.FireInsurance]: 'fire-simple',
  [DocumentType.CertificateOfTitle]: 'medal',
  [DocumentType.RatesAndTaxesReceiptInvoice]: 'money',
  [DocumentType.LetterFromToshaosHinterlandLocations]: 'scroll',
  [DocumentType.LetterOfAuthorisationFromUtilityBillOwner]: 'scroll',
  [DocumentType.LetterOfEmployment]: 'scroll',
  [DocumentType.PostMarkedEnvelopeWithCustomerNameAndAddress]: 'email',
  [DocumentType.LeaseAgreement]: 'house',
  [DocumentType.NotarizedStatement]: 'stamp',
  [DocumentType.CertificateOfLossOfNationality]: 'globe',
  [DocumentType.FormI407Proof]: 'scroll',
  [DocumentType.ProofOfCurrentAddress]: 'house',
  [DocumentType.TaxExemptionDocument]: 'receipt',
  [DocumentType.ResidencyVisaEntryPermitProof]: 'globe',
  [DocumentType.DiplomaticIdOrTaxExemptionLetter]: 'user',
  [DocumentType.BirthCertificate]: 'cake',
  [DocumentType.AdoptionOrder]: 'hand-heart',
  [DocumentType.Other]: 'receipt-cloud',
};

export const DOCUMENT_SECTION_TEXT: Partial<
  Record<DocumentSection, Partial<Record<TenantWithDefault, string>>>
> = {
  [DocumentSection.TIN]: {
    default: 'documentSection.TIN_Number.title',
  },
  [DocumentSection.ProofOfAddress]: {
    default: 'documentSection.Proof_of_Address.title',
  },
  [DocumentSection.ProofOfEmployment]: {
    default: 'documentSection.Proof_of_Employment.title',
  },
  [DocumentSection.ProofOfRelationship]: {
    default: 'documentSection.Proof_of_Relationship.title',
  },
  [DocumentSection.DocumentSign]: {
    default: 'documentSection.Document_Sign.title',
  },
  [DocumentSection.Identity]: { default: 'documentSection.Identity.title' },
  [DocumentSection.IdentityBack]: {
    default: 'documentSection.Identity_Back.title',
  },
  [DocumentSection.Selfie]: { default: 'documentSection.SN.title' },
  [DocumentSection.SecondID]: { default: 'documentSection.Second_ID.title' },
  [DocumentSection.SecondCitizenshipID]: {
    default: 'documentSection.Second_Citizenship_ID.title',
  },
  [DocumentSection.USCitizenshipRenouncement]: {
    default: 'documentSection.US_Citizenship_Renouncement.title',
  },
  [DocumentSection.TaxSupportingDocument]: {
    default: 'documentSection.Tax_Supporting_Document.title',
  },
};

export const LISTED_CATEGORIES_QUESTION = {
  question:
    'employmentForm.form.employmentRiskSector.SELF_EMPLOYED.question.label',
  description: 'categoriesQuestion.description',
};

export const SUPPORTED_DOCUMENT_EXTENSIONS = ['.png', '.jpg', '.jpeg', '.pdf'];
export const MAX_DOCUMENT_SIZE = 52428800;

export const DOCUMENT_STATUS_STEP_OR: Record<string, number> = {
  [DocumentStatus.Approved]: 1,
  [DocumentStatus.InReview]: 2,
  [DocumentStatus.Signing]: 3,
  [DocumentStatus.Expired]: 4,
  [DocumentStatus.Rejected]: 5,
  [DocumentStatus.Pending]: 6,
};

export const DOCUMENT_STATUS_STEP_AND: Record<string, number> = {
  [DocumentStatus.Rejected]: 1,
  [DocumentStatus.Pending]: 2,
  [DocumentStatus.Signing]: 3,
  [DocumentStatus.InReview]: 4,
  [DocumentStatus.Approved]: 5,
  [DocumentStatus.Expired]: 6,
};

export const CAYMAN_GROUPED_OTHER_DOCUMENT = [
  DocumentType.LeaseAgreement,
  DocumentType.LetterOfEmployment,
];

export const TWO_SIDED_DOCUMENTS: Partial<
  Record<DocumentSection, DocumentType[]>
> = {
  [DocumentSection.SecondID]: [
    DocumentType.Id,
    DocumentType.IdentityDriverLicense,
    DocumentType.VoterID,
    DocumentType.FirearmID,
    DocumentType.SeafarerID,
    DocumentType.WorkPermitCard,
    DocumentType.Nis,
    DocumentType.National_Health_Insurance,
    DocumentType.SocialSecurityCard,
    DocumentType.ImmigrationCard,
  ],
};

export const TWO_SIDED_GUIDANCE_BY_TYPE: Partial<
  Record<
    DocumentType,
    {
      front: string;
      back: string;
    }
  >
> = Object.fromEntries(
  TWO_SIDED_DOCUMENTS[DocumentSection.SecondID]?.map(type => [
    type,
    {
      front: 'verification.documentUploadBox.frontGuidanceSecondId',
      back: 'verification.documentUploadBox.backGuidanceSecondId',
    },
  ]) ?? [],
) as Partial<
  Record<
    DocumentType,
    {
      front: string;
      back: string;
    }
  >
>;
